import { type FC, useEffect } from "react";

// biome-ignore lint/style/noRestrictedImports: legacy suppression
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";

import { ForceReload } from "src/components/router/force-reload";
import { useUser } from "src/contexts/user-context";
import * as analytics from "src/lib/analytics";
import { Logrocket } from "src/lib/logrocket";
import { NavCommands } from "src/components/commandbar/nav-source";
import { useWorkspaceActions } from "./commands";
import { LockOutModal } from "./lock-out-modal";
import { Chameleon } from "src/lib/chameleon";
import { Koala } from "src/lib/koala";
import { Pylon } from "src/lib/pylon-widget";
import { AppProviders } from "./providers";
import { useDatadog } from "src/lib/datadog";
import { useRecentlyViewed } from "src/pages/home/components/use-recently-viewed";
import { RoutePreloadWarmer } from "src/router/route-preload-warmer";

export const App = ({ children }: { children: React.ReactNode }) => {
  return (
    <AppProviders>
      <Content>{children}</Content>
    </AppProviders>
  );
};

const Content: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, workspace, isLoading } = useUser();
  const { partnerUser } = useFlags();

  useEffect(() => {
    if (user && !user.not_member_of_current_workspace) {
      analytics.identify(String(user.id), {
        email: user.email,
        name: user.name,
      });

      if (user.current_workspace_id) {
        analytics.group(user.current_workspace_id);
      }
    }

    Sentry.setTag("user_id", String(user?.id));

    if (workspace) {
      Sentry.setTag("workspace_id", String(workspace?.id));
      Sentry.setTag("workspace_region", String(workspace?.region));
    }
  }, [user, workspace]);

  useEffect(() => {
    Sentry.setTag("partner_user", partnerUser);
  }, [partnerUser]);

  useEffect(() => {
    analytics.track("Application Opened", {
      window_height: window.innerHeight,
      window_width: window.innerWidth,
    });
  }, []);

  useEffect(() => {
    if (isLoading) return undefined;
    const skeleton = document.querySelector(".ht-skeleton");
    if (!skeleton) return undefined;
    if (!user) {
      // Unauthed: AuthLayout is rendering. Remove immediately. Fixes
      // landings on `/` (and any other path not covered by the inline
      // pathname check in index.html) that get redirected client-side
      // and would otherwise sit under a fixed-position skeleton forever.
      skeleton.remove();
      return undefined;
    }
    // Authed: let the real nav render before swapping, so the static
    // skeleton hands off smoothly instead of flashing.
    const t = setTimeout(() => skeleton.remove(), 1000);
    return () => clearTimeout(t);
  }, [user, isLoading]);

  useDatadog();
  useRecentlyViewed();

  return (
    <>
      <ForceReload />
      <Commands />
      {!import.meta.env.DEV && <Pylon />}
      <Logrocket />
      <Koala />
      <Chameleon />
      <RoutePreloadWarmer />
      <div id="portalAnchor" style={{ zIndex: 1500 }}></div>
      <LockOutModal />
      {children}
    </>
  );
};

const Commands: FC = () => {
  const { user, workspace } = useUser();
  useWorkspaceActions();

  // Registered with the app, not the palette, so they are listed from the palette's first frame.
  return user && workspace ? <NavCommands /> : null;
};
